// src/components/EventPage.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, onSnapshot,
  addDoc, updateDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

const SYM = { ok: '○', maybe: '△', ng: '✕' };
const SYM_COLOR = { ok: '#0F6E56', maybe: '#633806', ng: '#791F1F' };

// その日のGoogleカレンダーURLを生成
function calendarUrl(dateStr) {
  // dateStr: "2026-05-01" → Googleカレンダーのその日のビュー
  const d = dateStr.replace(/-/g, '');
  return `https://calendar.google.com/calendar/r/day/${d.slice(0,4)}/${d.slice(4,6)}/${d.slice(6,8)}`;
}

export default function EventPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [event,     setEvent]     = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [notFound,  setNotFound]  = useState(false);
  const [tab,       setTab]       = useState('vote');

  useEffect(() => {
    getDoc(doc(db, 'events', id)).then(snap => {
      if (!snap.exists()) { setNotFound(true); setLoading(false); return; }
      setEvent({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'events', id, 'responses'), snap => {
      setResponses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [id]);

  const isHost = user && event && user.uid === event.hostUid;
  const shareUrl = window.location.href;

  async function copyUrl() {
    try { await navigator.clipboard.writeText(shareUrl); alert('URLをコピーしました'); } catch {}
  }

  async function closeEvent() {
    if (!window.confirm('このイベントを締め切りますか？')) return;
    await updateDoc(doc(db, 'events', id), { closedAt: serverTimestamp() });
    setEvent(ev => ({ ...ev, closedAt: true }));
  }

  async function deleteEvent() {
    if (!window.confirm('このイベントを削除しますか？この操作は取り消せません。')) return;
    await deleteDoc(doc(db, 'events', id));
    navigate('/my-events');
  }

  if (loading)  return <div className="page"><div className="container"><div className="spinner" /></div></div>;
  if (notFound) return <div className="page"><div className="container"><div className="card">イベントが見つかりません</div></div></div>;

  return (
    <div className="page">
      <div className="container">
        <div className="card">
          <div className="flex items-center justify-between mb-1">
            <h1 style={{ fontSize: 20, fontWeight: 600 }}>{event.title}</h1>
            {event.closedAt && <span className="badge" style={{ background: '#FCEBEB', color: '#791F1F' }}>締め切り済み</span>}
          </div>
          {event.memo && <p className="text-sm text-muted mb-2" style={{ whiteSpace: 'pre-wrap' }}>{event.memo}</p>}
          <div className="flex gap-2 text-sm text-muted" style={{ flexWrap: 'wrap' }}>
            <span>主催：{event.hostName}</span>
            {event.deadline && <span>・締め切り：{event.deadline}</span>}
            <span>・回答 {responses.length}件</span>
          </div>
          <div className="share-box mt-2">
            <span className="share-url">{shareUrl}</span>
            <button className="btn btn-sm" onClick={copyUrl}>コピー</button>
          </div>
        </div>

        <div style={{ display: 'flex', marginBottom: '1rem', background: '#fff', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: 10, overflow: 'hidden' }}>
          {[['vote', '回答する'], ['result', '結果を見る']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              flex: 1, padding: '10px', fontSize: 14, border: 'none', cursor: 'pointer',
              background: tab === key ? '#1D9E75' : '#fff',
              color: tab === key ? '#fff' : '#555',
              fontWeight: tab === key ? 500 : 400,
            }}>{label}</button>
          ))}
        </div>

        {tab === 'vote' && (
          <VoteForm event={event} responses={responses} user={user} eventId={id} />
        )}
        {tab === 'result' && (
          <ResultView event={event} responses={responses} isHost={isHost}
            onClose={closeEvent} onDelete={deleteEvent} eventId={id} />
        )}
      </div>
    </div>
  );
}

function VoteForm({ event, responses, user, eventId }) {
  const [name,      setName]      = useState(user?.displayName || '');
  const [choices,   setChoices]   = useState({});
  const [comment,   setComment]   = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const myRes = user ? responses.find(r => r.uid === user.uid) : null;

  useEffect(() => {
    if (myRes) {
      setName(myRes.name);
      setChoices(myRes.choices || {});
      setComment(myRes.comment || '');
    }
  }, [myRes]);

  function setChoice(slotId, val) {
    setChoices(prev => ({ ...prev, [slotId]: val }));
  }

  async function submit() {
    if (!name.trim()) { setError('名前を入力してください'); return; }
    if (event.slots.some(s => !choices[s.id])) { setError('すべての日程に回答してください'); return; }
    setError('');
    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        uid: user?.uid || null,
        photoURL: user?.photoURL || null,
        choices,
        comment: comment.trim(),
        updatedAt: serverTimestamp(),
      };
      if (myRes) {
        await updateDoc(doc(db, 'events', eventId, 'responses', myRes.id), payload);
      } else {
        await addDoc(collection(db, 'events', eventId, 'responses'), {
          ...payload, createdAt: serverTimestamp(),
        });
      }
      setSubmitted(true);
    } catch (e) {
      setError('送信に失敗しました。もう一度お試しください。');
    }
    setLoading(false);
  }

  if (event.closedAt) {
    return <div className="card"><div className="msg-info">このイベントは締め切られました。結果タブで集計をご確認ください。</div></div>;
  }

  if (submitted) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
        <div className="font-medium" style={{ marginBottom: 6 }}>回答しました</div>
        <p className="text-sm text-muted">ご協力ありがとうございます</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-title">出欠を入力してください</div>

      {user ? (
        <div className="flex items-center gap-2 mb-2" style={{ padding: '8px 12px', background: '#E1F5EE', borderRadius: 8 }}>
          {user.photoURL && <img src={user.photoURL} alt="" className="avatar" referrerPolicy="no-referrer" />}
          <span className="text-sm">{user.displayName || user.email} としてログイン中</span>
        </div>
      ) : (
        <div className="mb-2">
          <label className="field-label">お名前 *</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="氏名を入力" style={{ maxWidth: 280 }} />
          <p className="text-sm text-muted mt-1">
            Googleアカウントをお持ちの場合は<a href="/">ログイン</a>すると回答の修正が可能です
          </p>
        </div>
      )}

      <hr className="divider" />

      <div style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: 13 }}>
        {[['ok','○ 参加可'], ['maybe','△ 未定'], ['ng','✕ 不参加']].map(([k, l]) => (
          <span key={k} style={{ color: SYM_COLOR[k] }}>{l}</span>
        ))}
      </div>

      {/* 候補日ごとの行 */}
      {(() => {
        // 日付ごとにグループ化してカレンダーリンクをまとめる
        const datesSeen = new Set();
        return event.slots.map(slot => {
          const showCalLink = !datesSeen.has(slot.date);
          datesSeen.add(slot.date);
          return (
            <div key={slot.id} style={{ padding: '12px 0', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>
              <div className="flex items-center justify-between">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 500 }}>{slot.label}</span>
                    <span className="text-sm text-muted">{slot.time}</span>
                    {showCalLink && (
                      <a
                        href={calendarUrl(slot.date)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 11, color: '#185FA5',
                          background: '#E6F1FB', border: '0.5px solid #C3D9F5',
                          borderRadius: 4, padding: '2px 7px', textDecoration: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        📅 カレンダーで確認
                      </a>
                    )}
                  </div>
                </div>
                <div className="vote-btns" style={{ marginLeft: 12 }}>
                  {['ok', 'maybe', 'ng'].map(v => (
                    <button key={v}
                      className={`avail-btn ${choices[slot.id] === v ? `active-${v}` : ''}`}
                      onClick={() => setChoice(slot.id, v)}>
                      {SYM[v]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        });
      })()}

      <div className="mt-2">
        <label className="field-label">コメント（任意）</label>
        <input type="text" value={comment} onChange={e => setComment(e.target.value)}
          placeholder="例：16時以降なら可能です" />
      </div>

      {error && <div className="msg-error mt-2">{error}</div>}

      <button className="btn btn-primary btn-full mt-2" onClick={submit} disabled={loading}>
        {loading
          ? <><span className="spinner" style={{ width: 16, height: 16 }} /> 送信中…</>
          : myRes ? '回答を更新する' : '回答を送信する'}
      </button>
    </div>
  );
}

function ResultView({ event, responses, isHost, onClose, onDelete, eventId }) {
  const scores = event.slots.map(slot => {
    const ok    = responses.filter(r => r.choices?.[slot.id] === 'ok').length;
    const maybe = responses.filter(r => r.choices?.[slot.id] === 'maybe').length;
    const ng    = responses.filter(r => r.choices?.[slot.id] === 'ng').length;
    return { slot, ok, maybe, ng, score: ok * 2 + maybe };
  }).sort((a, b) => b.score - a.score);

  const maxScore = Math.max(...scores.map(s => s.score), 1);

  async function deleteResponse(resId) {
    if (!window.confirm('この回答を削除しますか？')) return;
    await deleteDoc(doc(db, 'events', eventId, 'responses', resId));
  }

  return (
    <>
      <div className="card">
        <div className="card-title">集計結果（{responses.length}名回答）</div>
        {scores.length === 0 ? (
          <p className="text-sm text-muted">まだ回答がありません</p>
        ) : scores.map((s, i) => (
          <div key={s.slot.id} style={{ padding: '10px 0', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <span style={{ fontWeight: 500 }}>{s.slot.label}</span>
                <span className="text-sm text-muted" style={{ marginLeft: 6 }}>{s.slot.time}</span>
              </div>
              {i === 0 && responses.length > 0 && <span className="badge badge-best">最多○</span>}
            </div>
            <div className="flex gap-3 text-sm" style={{ marginBottom: 6 }}>
              <span style={{ color: '#0F6E56' }}>○ {s.ok}名</span>
              <span style={{ color: '#633806' }}>△ {s.maybe}名</span>
              <span style={{ color: '#791F1F' }}>✕ {s.ng}名</span>
            </div>
            <div className="result-bar">
              <div className="result-fill" style={{ width: `${Math.round(s.score / maxScore * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {responses.length > 0 && (
        <div className="card">
          <div className="card-title">回答一覧</div>
          <div className="resp-table-wrap">
            <table className="resp-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>名前</th>
                  {event.slots.map(s => (
                    <th key={s.id}>{s.label}<br />
                      <span style={{ fontWeight: 400, color: '#aaa' }}>{s.time}</span>
                    </th>
                  ))}
                  <th>コメント</th>
                  {isHost && <th></th>}
                </tr>
              </thead>
              <tbody>
                {responses.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        {r.photoURL
                          ? <img src={r.photoURL} alt="" className="avatar" referrerPolicy="no-referrer" />
                          : <div className="avatar-initials" style={{ width: 24, height: 24, fontSize: 11 }}>{(r.name || '?')[0]}</div>
                        }
                        {r.name}
                      </div>
                    </td>
                    {event.slots.map(s => (
                      <td key={s.id} style={{ color: SYM_COLOR[r.choices?.[s.id] || 'ng'], fontWeight: 500 }}>
                        {SYM[r.choices?.[s.id] || 'ng']}
                      </td>
                    ))}
                    <td className="text-sm text-muted">{r.comment || '—'}</td>
                    {isHost && (
                      <td><button className="btn btn-sm btn-danger" onClick={() => deleteResponse(r.id)}>削除</button></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isHost && (
        <div className="card">
          <div className="card-title">主催者メニュー</div>
          <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
            {!event.closedAt && (
              <button className="btn" onClick={onClose}>回答を締め切る</button>
            )}
            <button className="btn btn-danger" onClick={onDelete}>イベントを削除する</button>
          </div>
        </div>
      )}
    </>
  );
}
